import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import * as _ from 'lodash';
import { Dictionary } from 'lodash';
import { Collection } from '../../../services/collection.model';
import { CollectionService } from '../../../services/collection.service';
import { CollectionStatus, CollectionType, PaymentMethod } from '../../../shared/enums/smarthub-enums';
import { ProgressStates } from '../progress-states.model';
import { DashboardFilters } from '../../dashboard/filters/dashboard-filters.model';
import { FiltersService } from '../../dashboard/filters/filters.service';
import { SettingsService } from '../../../shared/services/settings/settings.service';
import { BaseObserver } from '../../../shared/baseobserver';
import { filter, skipWhile, takeUntil } from 'rxjs/operators';
import { Util } from '../../../../util';

@Component({
  selector: 'ssg-at-a-glance-collections',
  templateUrl: './at-a-glance-collections.component.html',
  styleUrls: ['../at-a-glance.component.less']
})
export class AtAGlanceCollectionsComponent extends BaseObserver implements OnInit {
  CollectionStatus = CollectionStatus;
  allOtherStatuses = -1;

  @Output() progressStatesChanged = new EventEmitter<ProgressStates>();

  invalidStatuses = [CollectionStatus.Unknown, CollectionStatus.Combined];
  invalidCollectionTypes = [CollectionType.Tax, CollectionType.TaxRefund, CollectionType.TaxPenaltyInterest, CollectionType.Garnishments];
  invalidMethods = [ PaymentMethod.DirectWireCredit, PaymentMethod.AchCredit ];
  statusOrder = [CollectionStatus.Returned, this.allOtherStatuses,  CollectionStatus.Collected, CollectionStatus.Voided];

  stateChanged = new EventEmitter<any>();
  expanded = {};
  private _allExpanded = false;
  private _sectionExpanded = false;
  collections: Dictionary<Collection[]>;

  constructor(
    private collectionService: CollectionService,
    private filtersService: FiltersService,
    private settings: SettingsService) {
    super();
  }

  ngOnInit() {
    this.filtersService.getFilters()
      .pipe(
        filter((filt) => filt.payDate !== Util.MIN_DATE),
        takeUntil(this.destroy$))
      .subscribe((filters) => {
          this.loadCollections(filters);
      });
    this.sectionExpanded = true;
  }

  loadCollections(filters: DashboardFilters): void {
    if (!filters) {
      this.collections = undefined;
      this.progressStatesChanged.emit({
        complete: false,
        inProgress: false
      });
      return;
    }
    this.collectionService.getCollections(this.settings.tenantId, filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe(colls => {
        const filteredColls = _(colls).filter(coll => coll.totalNettedAmount !== 0
          && !this.invalidStatuses.includes(coll.status)
          && !this.invalidCollectionTypes.includes(coll.type)
          && !this.invalidMethods.includes(coll.paymentMethod))
          .orderBy(['name', 'totalNettedAmount'], ['asc', 'desc']);

        const foundNettedColls: { [index: string]: boolean } = {};
        const nettedFilteredCollections = filteredColls.filter(collection => {
          if (collection.nettedCollectionCount <= 1 || !collection.nettedCollectionId) {
            return true;
          }

          if (!foundNettedColls[collection.nettedCollectionId]) {
            foundNettedColls[collection.nettedCollectionId] = true;
            return true;
          }

          return false;
        });

        const groupedColls = nettedFilteredCollections
          .groupBy(d => {
            if (_.includes([CollectionStatus.Returned, CollectionStatus.Collected, CollectionStatus.Voided], d.status)) {
              return d.status;
            } else {
              return this.allOtherStatuses;
            }
          })
          .value();

        this.collections = groupedColls;

        const complete = this.isThisComplete(filteredColls.value());
        const inProgress = !complete && this.isThisInProgress(filteredColls.value());

        this.progressStatesChanged.emit({
          complete,
          inProgress
        });
      });
  }

  isThisComplete(colls: Collection[]): boolean {
    return !_.isEmpty(colls) &&
      _(colls).every(coll =>
        _.includes(
          [CollectionStatus.Collected, CollectionStatus.Voided, CollectionStatus.Created, CollectionStatus.Pending],
          coll.status));
  }

  isThisInProgress(colls: Collection[]): boolean {
    return !_.isEmpty(colls) &&
      _(colls).some(coll => _.includes(
        [CollectionStatus.PartiallyCollected, CollectionStatus.InTransit, CollectionStatus.Returned,
          CollectionStatus.Collected, CollectionStatus.Voided],
        coll.status));
  }

  get noCollections(): boolean {
    return _.isEmpty(this.collections);
  }

  get isApiForNettedCollectionsEnabled(): boolean {
    return this.collectionService.isApiForNettedCollectionsEnabled;
  }

  get allExpanded(): boolean {
    return this._allExpanded;
  }

  @Input() set allExpanded(value: boolean) {
    if (value === this._allExpanded) {
      return;
    }

    this._allExpanded = value;
    this.sectionExpanded = value;
    this.stateChanged.emit();
  }

  get sectionExpanded(): boolean {
    return this._sectionExpanded;
  }

  @Input() set sectionExpanded(value: boolean) {
    if (value === this._sectionExpanded) {
      return;
    }

    this._sectionExpanded = value;
    this.stateChanged.emit();
  }

  getCurrencyStyleClass(status: CollectionStatus) {
    switch (status) {
      case CollectionStatus.Returned:
        return 'text-danger';
      case CollectionStatus.Voided:
        return 'text-muted strikethrough';
      default:
        return '';
    }
  }
}
